import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Textarea } from '@jaylabs/ui';
import HeaderActionsBar from "../components/HeaderActionsBar";
import ChatSidebar from "../components/ChatSidebar";
import Logo from "../components/Logo";
import { API_URL } from "../utils/apiUrl";
import './CreateTask.css';

function CreateTask() {
  const navigate = useNavigate();
  const defaultPoints = 10;
  const dueDateRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: defaultPoints,
    difficulty: 'medium',
    priority: 'normal',
    category: '',
    category_id: '',
    status: 'pending',
    project_id: '',
    due_date: '',
    tags: [],
    override_reason: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [projects, setProjects] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#8B5CF6');
  const [creatingProject, setCreatingProject] = useState(false);
  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setProjects(data.projects);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/inbox?unreadOnly=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCategories();
    fetchUnreadCount();
  }, [fetchProjects, fetchCategories]);

  useEffect(() => {
    if (chatOpen) {
      setUnreadCount(0);
    }
  }, [chatOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category_id') {
      const selected = categories.find(c => String(c.id) === value);
      setFormData(prev => ({
        ...prev,
        category_id: value,
        category: selected ? selected.name : prev.category
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: name === 'points' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setCategories(prev => [...prev, data.category]);
        setFormData(prev => ({ ...prev, category_id: String(data.category.id), category: data.category.name }));
        setNewCategoryName('');
      }
    } catch (err) {
      console.error('Error creating category:', err);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setCreatingProject(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newProjectName.trim(), color: newProjectColor })
      });
      const data = await res.json();
      if (res.ok) {
        setProjects(prev => [...prev, data.project]);
        setFormData(prev => ({ ...prev, project_id: String(data.project.id) }));
        setNewProjectName('');
      }
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const hasPointOverride = Number(formData.points) !== defaultPoints;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (hasPointOverride && !formData.override_reason.trim()) {
      setError('Override reason required when changing points from the default.');
      return;
    }

    const payload = {
      ...formData,
      points: hasPointOverride ? Number(formData.points) : undefined,
      override_reason: hasPointOverride ? formData.override_reason.trim() : undefined,
    };

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Task created successfully!');
        setFormData({
          title: '',
          description: '',
          points: defaultPoints,
          difficulty: 'medium',
          priority: 'normal',
          category: '',
          category_id: '',
          status: 'pending',
          project_id: '',
          due_date: '',
          tags: [],
          override_reason: ''
        });
        setTagInput('');
        setTimeout(() => {
          navigate('/tasks');
        }, 1500);
      } else {
        setError(data.message || 'Failed to create task');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error:', err);
    }
  };

  return (
    <div
      className="dd-create-task-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      <div className="dd-container" style={{ maxWidth: '48rem' }}>
        {/* Top Bar */}
        <div className="dd-hstack dd-mb-6" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Logo size="md" />
          <HeaderActionsBar>
            <button
              className="dd-icon-btn"
              aria-label="Back to tasks"
              title="Back to tasks"
              onClick={() => navigate('/tasks')}
            >
              📋
            </button>
            <button
              className="dd-icon-btn"
              aria-label="Dashboard"
              title="Dashboard"
              onClick={() => navigate('/dashboard')}
            >
              🏠
            </button>
            {localStorage.getItem('hideFriends') !== 'true' && (
              <button
                className="dd-icon-btn"
                aria-label="Friends"
                title="Friends"
                onClick={() => navigate('/friends')}
              >
                👥
              </button>
            )}
            {localStorage.getItem('hideFriends') !== 'true' && (
              <Button variant="primary" size="lg" onClick={() => setChatOpen(true)}>
                💬 Chat{unreadCount > 0 ? ` (${unreadCount})` : ''}
              </Button>
            )}
          </HeaderActionsBar>
        </div>
        <ChatSidebar isOpen={chatOpen} onClose={() => setChatOpen(false)} />

        <div className="dd-vstack dd-gap-6">
          <h1 className="dd-heading-xl">Create New Task</h1>

          {/* Form Card */}
          <div className="dd-card" style={{ padding: 'var(--dd-space-8)' }}>
            <form onSubmit={handleSubmit}>
              <div className="dd-vstack dd-gap-6">
                {/* Title */}
                <div className="dd-form-field">
                  <label className="dd-label">Task Title *</label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter task title"
                    size="lg"
                    required
                  />
                </div>

                {/* Description */}
                <div className="dd-form-field">
                  <label className="dd-label">Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter task description (optional)"
                    rows={4}
                  />
                </div>

                {/* Points */}
                <div className="dd-form-field">
                  <label className="dd-label">Points</label>
                  <Input
                    name="points"
                    type="number"
                    value={formData.points}
                    onChange={handleChange}
                    min="1"
                    max="1000"
                    size="lg"
                  />
                  <p className="dd-form-hint">
                    How many points is this task worth? AI suggests based on complexity.
                  </p>
                </div>

                {/* Override Reason */}
                {hasPointOverride && (
                  <div className="dd-form-field">
                    <label className="dd-label">Override Reason</label>
                    <Textarea
                      name="override_reason"
                      value={formData.override_reason}
                      onChange={handleChange}
                      placeholder="Why are you adjusting the points from the suggested value?"
                      rows={2}
                    />
                    <p className="dd-form-hint-warn">
                      ⚠️ Required when overriding suggested points
                    </p>
                  </div>
                )}

                {/* Difficulty */}
                <div className="dd-form-field">
                  <label className="dd-label">Difficulty</label>
                  <select
                    className="dd-select"
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                {/* Priority */}
                <div className="dd-form-field">
                  <label className="dd-label">Priority</label>
                  <select
                    className="dd-select"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                  </select>
                  <p className="dd-form-hint">
                    Set importance to help sort upcoming and active tasks.
                  </p>
                </div>

                {/* Category */}
                <div className="dd-form-field">
                  <label className="dd-label">Category</label>
                  <select
                    className="dd-select"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                  >
                    <option value="">Choose category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <div className="dd-hstack dd-gap-2">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); } }}
                      placeholder="New category name"
                      size="sm"
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreateCategory}
                      disabled={!newCategoryName.trim() || creatingCategory}
                    >
                      + Add
                    </Button>
                  </div>
                </div>

                {/* Status */}
                <div className="dd-form-field">
                  <label className="dd-label">Status</label>
                  <select
                    className="dd-select"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Project */}
                <div className="dd-form-field">
                  <label className="dd-label">Project (Optional)</label>
                  <select
                    className="dd-select"
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleChange}
                  >
                    <option value="">No Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                  <div className="dd-hstack dd-gap-2">
                    <Input
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateProject(); } }}
                      placeholder="New project name"
                      size="sm"
                    />
                    <input
                      type="color"
                      value={newProjectColor}
                      onChange={(e) => setNewProjectColor(e.target.value)}
                      className="dd-color-input"
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreateProject}
                      disabled={!newProjectName.trim() || creatingProject}
                    >
                      + Add
                    </Button>
                  </div>
                </div>

                {/* Due Date */}
                <div className="dd-form-field">
                  <label className="dd-label">Due Date (Optional)</label>
                  <input
                    name="due_date"
                    type="datetime-local"
                    ref={dueDateRef}
                    value={formData.due_date}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '1rem',
                      border: '1px solid var(--dd-border-input, #d1d5db)',
                      borderRadius: '8px',
                      background: 'var(--dd-bg-input, #fff)',
                      color: 'var(--dd-text-input, #111)',
                      cursor: 'pointer'
                    }}
                  />
                  <p className="dd-form-hint">Set a deadline for this task</p>
                </div>

                {/* Tags */}
                <div className="dd-form-field">
                  <label className="dd-label">Tags (Optional)</label>
                  <div className="dd-hstack dd-gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      placeholder="Add a tag and press Enter"
                      size="lg"
                    />
                    <Button variant="primary" size="lg" onClick={handleAddTag}>
                      Add
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="dd-hstack dd-gap-2 dd-hstack-wrap">
                      {formData.tags.map((tag, idx) => (
                        <span key={idx} className="dd-tag">
                          #{tag}
                          <button
                            type="button"
                            className="dd-tag-remove"
                            onClick={() => handleRemoveTag(tag)}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="dd-form-hint">Add tags to organize and filter tasks</p>
                </div>

                {/* Error */}
                {error && (
                  <div className="dd-alert-error">
                    <p>{error}</p>
                  </div>
                )}

                {/* Success */}
                {success && (
                  <div className="dd-alert-success">
                    <p>{success}</p>
                  </div>
                )}

                {/* Submit */}
                <Button type="submit" variant="success" size="lg" className="dd-full-width">
                  Create Task
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTask;
